// src/models/chatModels/chatMessage.js

// ── Message Status ────────────────────────────────────────────────────────────

export const MessageStatus = {
  SENDING: 'sending',
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
  FAILED: 'failed',
};

export const statusFromString = (value) => {
  switch (value) {
    case 'sent': return MessageStatus.SENT;
    case 'delivered': return MessageStatus.DELIVERED;
    case 'read': return MessageStatus.READ;
    case 'failed': return MessageStatus.FAILED;
    case 'sending':
    default: return MessageStatus.SENDING;
  }
};

export const statusToString = (status) => status;

// ── Message Type ──────────────────────────────────────────────────────────────

export const MessageType = {
  TEXT: 'text',
  IMAGE: 'image',
  AUDIO: 'audio',
  VIDEO: 'video',
  FILE: 'file',
  DELETED: 'deleted',
};

export const typeFromString = (value) => {
  switch (value) {
    case 'image': return MessageType.IMAGE;
    case 'audio': return MessageType.AUDIO;
    case 'video': return MessageType.VIDEO;
    case 'file': return MessageType.FILE;
    case 'deleted': return MessageType.DELETED;
    case 'text':
    default: return MessageType.TEXT;
  }
};

export const typeToString = (type) => type;

// ── ReplyMetadata ─────────────────────────────────────────────────────────────

export class ReplyMetadata {
  constructor({
    replyToId,
    replyToContent = null,
    replyToSenderId = null,
    replyToType = null,
    replyToAttachmentUrl = null,
  }) {
    this.replyToId = replyToId;
    this.replyToContent = replyToContent;
    this.replyToSenderId = replyToSenderId;
    this.replyToType = replyToType;
    this.replyToAttachmentUrl = replyToAttachmentUrl;
  }

  toJSON() {
    return {
      replyToId: this.replyToId,
      replyToContent: this.replyToContent,
      replyToSenderId: this.replyToSenderId,
      replyToType: this.replyToType,
      replyToAttachmentUrl: this.replyToAttachmentUrl,
    };
  }

  static fromJSON(json) {
    return new ReplyMetadata({
      replyToId: json.replyToId || '',
      replyToContent: json.replyToContent || null,
      replyToSenderId: json.replyToSenderId || null,
      replyToType: json.replyToType || null,
      replyToAttachmentUrl: json.replyToAttachmentUrl || null,
    });
  }
}

// ── ChatMessage ───────────────────────────────────────────────────────────────

export class ChatMessage {
  constructor({
    id,
    conversationId,
    senderId,
    receiverId,
    content,
    timestamp,
    status = MessageStatus.SENDING,
    messageType = MessageType.TEXT,
    attachmentUrl = null,
    attachmentType = null,
    attachmentName = null,
    attachmentSize = null,
    audioDuration = null,
    imageWidth = null,
    imageHeight = null,
    localFilePath = null,
    replyToMessageId = null,
    replyToContent = null,
    replyToSenderId = null,
    replyToType = null,
    replyToAttachmentUrl = null,
    isForwarded = false,
    seenBy = [],
    deletedFor = null,
    isDeleted = false,
    isDeletedForMe = false,
    unreadCount = 0,
  }) {
    this.id = id;
    this.conversationId = conversationId;
    this.senderId = senderId;
    this.receiverId = receiverId;
    this.content = content;
    this.timestamp = timestamp;
    this.status = status;
    this.messageType = messageType;
    this.attachmentUrl = attachmentUrl;
    this.attachmentType = attachmentType;
    this.attachmentName = attachmentName;
    this.attachmentSize = attachmentSize;
    this.audioDuration = audioDuration;
    this.imageWidth = imageWidth;
    this.imageHeight = imageHeight;
    this.localFilePath = localFilePath;
    this.replyToMessageId = replyToMessageId;
    this.replyToContent = replyToContent;
    this.replyToSenderId = replyToSenderId;
    this.replyToType = replyToType;
    this.replyToAttachmentUrl = replyToAttachmentUrl;
    this.isForwarded = isForwarded;
    this.seenBy = seenBy;
    this.deletedFor = deletedFor;
    this.isDeleted = isDeleted;
    this.isDeletedForMe = isDeletedForMe;
    this.unreadCount = unreadCount;
  }

  // ── Convenience getters ───────────────────────────────────────────────────

  isMine(currentUserId) {
    return this.senderId === currentUserId;
  }

  get hasAttachment() {
    return this.attachmentUrl !== null || this.localFilePath !== null;
  }

  get isImage() {
    return this.messageType === MessageType.IMAGE;
  }

  get isAudio() {
    return this.messageType === MessageType.AUDIO;
  }

  get isVideo() {
    return this.messageType === MessageType.VIDEO;
  }

  get isFile() {
    return this.messageType === MessageType.FILE;
  }

  get isText() {
    return this.messageType === MessageType.TEXT;
  }

  get replyToId() {
    return this.replyToMessageId;
  }

  get isRead() {
    return this.seenBy.includes(this.receiverId);
  }

  get isDelivered() {
    return this.seenBy.length >= 1;
  }

  // ── fromJson ──────────────────────────────────────────────────────────────

  static fromJson(json) {
    let timestamp = new Date();
    if (json.timestamp) {
      const parsed = new Date(json.timestamp);
      if (!isNaN(parsed.getTime())) {
        timestamp = parsed;
      }
    }

    return new ChatMessage({
      id: json.id?.toString() || '',
      conversationId: json.conversationId?.toString() || '',
      senderId: json.senderId?.toString() || '',
      receiverId: json.receiverId?.toString() || '',
      content: json.content?.toString() || '',
      timestamp: timestamp,
      status: statusFromString(json.status?.toString()),
      messageType: typeFromString(json.messageType?.toString()),
      attachmentUrl: json.attachmentUrl?.toString() || null,
      attachmentType: json.attachmentType?.toString() || null,
      attachmentName: json.attachmentName?.toString() || null,
      attachmentSize: json.attachmentSize || null,
      audioDuration: json.audioDuration || null,
      imageWidth: json.imageWidth || null,
      imageHeight: json.imageHeight || null,
      localFilePath: json.localFilePath?.toString() || null,
      replyToMessageId: json.replyToMessageId?.toString() || null,
      replyToContent: json.replyToContent?.toString() || null,
      replyToSenderId: json.replyToSenderId?.toString() || null,
      replyToType: json.replyToType?.toString() || null,
      replyToAttachmentUrl: json.replyToAttachmentUrl?.toString() || null,
      isForwarded: json.isForwarded || false,
      seenBy: json.seenBy || [],
      deletedFor: json.deletedFor || null,
      isDeleted: json.isDeleted || false,
      isDeletedForMe: json.isDeletedForMe || false,
      unreadCount: json.unreadCount || 0,
    });
  }

  // ── toJson ────────────────────────────────────────────────────────────────

  toJson() {
    const json = {
      id: this.id,
      conversationId: this.conversationId,
      senderId: this.senderId,
      receiverId: this.receiverId,
      content: this.content,
      timestamp: this.timestamp.toISOString(),
      status: this.status,
      messageType: this.messageType,
      isForwarded: this.isForwarded,
      seenBy: this.seenBy,
      isDeleted: this.isDeleted,
      isDeletedForMe: this.isDeletedForMe,
      unreadCount: this.unreadCount,
    };

    if (this.attachmentUrl) json.attachmentUrl = this.attachmentUrl;
    if (this.attachmentType) json.attachmentType = this.attachmentType;
    if (this.attachmentName) json.attachmentName = this.attachmentName;
    if (this.attachmentSize) json.attachmentSize = this.attachmentSize;
    if (this.audioDuration) json.audioDuration = this.audioDuration;
    if (this.imageWidth) json.imageWidth = this.imageWidth;
    if (this.imageHeight) json.imageHeight = this.imageHeight;
    if (this.localFilePath) json.localFilePath = this.localFilePath;
    if (this.replyToMessageId) json.replyToMessageId = this.replyToMessageId;
    if (this.replyToContent) json.replyToContent = this.replyToContent;
    if (this.replyToSenderId) json.replyToSenderId = this.replyToSenderId;
    if (this.replyToType) json.replyToType = this.replyToType;
    if (this.replyToAttachmentUrl) json.replyToAttachmentUrl = this.replyToAttachmentUrl;
    if (this.deletedFor) json.deletedFor = this.deletedFor;

    return json;
  }

  // ── copyWith ──────────────────────────────────────────────────────────────

  copyWith({
    id,
    conversationId,
    senderId,
    receiverId,
    content,
    timestamp,
    status,
    messageType,
    attachmentUrl,
    attachmentType,
    attachmentName,
    attachmentSize,
    audioDuration,
    imageWidth,
    imageHeight,
    localFilePath,
    replyToMessageId,
    replyToContent,
    replyToSenderId,
    replyToType,
    replyToAttachmentUrl,
    isForwarded,
    seenBy,
    deletedFor,
    isDeleted,
    isDeletedForMe,
    unreadCount,
  }) {
    return new ChatMessage({
      id: id ?? this.id,
      conversationId: conversationId ?? this.conversationId,
      senderId: senderId ?? this.senderId,
      receiverId: receiverId ?? this.receiverId,
      content: content ?? this.content,
      timestamp: timestamp ?? this.timestamp,
      status: status ?? this.status,
      messageType: messageType ?? this.messageType,
      attachmentUrl: attachmentUrl ?? this.attachmentUrl,
      attachmentType: attachmentType ?? this.attachmentType,
      attachmentName: attachmentName ?? this.attachmentName,
      attachmentSize: attachmentSize ?? this.attachmentSize,
      audioDuration: audioDuration ?? this.audioDuration,
      imageWidth: imageWidth ?? this.imageWidth,
      imageHeight: imageHeight ?? this.imageHeight,
      localFilePath: localFilePath ?? this.localFilePath,
      replyToMessageId: replyToMessageId ?? this.replyToMessageId,
      replyToContent: replyToContent ?? this.replyToContent,
      replyToSenderId: replyToSenderId ?? this.replyToSenderId,
      replyToType: replyToType ?? this.replyToType,
      replyToAttachmentUrl: replyToAttachmentUrl ?? this.replyToAttachmentUrl,
      isForwarded: isForwarded ?? this.isForwarded,
      seenBy: seenBy ?? this.seenBy,
      deletedFor: deletedFor ?? this.deletedFor,
      isDeleted: isDeleted ?? this.isDeleted,
      isDeletedForMe: isDeletedForMe ?? this.isDeletedForMe,
      unreadCount: unreadCount ?? this.unreadCount,
    });
  }

  // ── Local serialization ───────────────────────────────────────────────────

  toRaw() {
    return JSON.stringify(this.toJson());
  }

  static fromRaw(source) {
    const decoded = JSON.parse(source);
    if (decoded && typeof decoded === 'object') {
      return ChatMessage.fromJson(decoded);
    }
    throw new Error('Invalid ChatMessage format');
  }

  toString() {
    return `ChatMessage(id: ${this.id}, sender: ${this.senderId}, type: ${this.messageType}, status: ${this.status}, content: ${this.content})`;
  }
}

export default ChatMessage;